/**
 * Authenticated landing — shown to logged-in users after signup or login.
 * Renders a glowing welcome + four "coming soon" product offering tiles.
 * Route protection happens at the router level via RequireAuth.
 */
import { useAuth } from "../hooks/useAuth";
import "./DashboardPage.css";

const OFFERINGS = [
  {
    key: "legal",
    title: "Legal Services",
    blurb: "AI-driven contract review, regulatory navigation, compliance automation.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M12 3v18M8 6h8M5 21h14M6 6l-3 9a4 4 0 0 0 8 0L8 6M18 6l-3 9a4 4 0 0 0 8 0l-3-9" />
      </svg>
    ),
  },
  {
    key: "biotech",
    title: "Biotechnology",
    blurb: "Lab data pipelines, knowledge engineering, generative AI for research workflows.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M9 2v6l-5 9a4 4 0 0 0 4 5h8a4 4 0 0 0 4-5l-5-9V2M9 2h6M8 14h8" />
      </svg>
    ),
  },
  {
    key: "health",
    title: "Health Services",
    blurb: "Cardiac event prediction, EHR analytics, BI dashboards built for clinicians.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M3 12h4l2-5 4 10 2-5h6" />
      </svg>
    ),
  },
  {
    key: "pharma",
    title: "Pharmacology",
    blurb: "Drug target identification, clinical trial optimization, regulatory submission assistants.",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <rect x="3" y="9" width="18" height="11" rx="3" />
        <path d="M3 14h18M7 9V5a5 5 0 0 1 10 0v4" />
      </svg>
    ),
  },
];

export function DashboardPage() {
  const { user } = useAuth();
  const firstName = user?.firstName ?? "there";

  return (
    <section className="dashboard">
      {/* Decorative glowing halo behind the welcome text */}
      <div className="dashboard__halo" aria-hidden />
      <div className="dashboard__stars" aria-hidden>
        {Array.from({ length: 24 }, (_, i) => (
          <span
            key={i}
            className="dashboard__star"
            style={{
              left: `${(i * 41) % 100}%`,
              top: `${(i * 67) % 100}%`,
              animationDelay: `${(i % 8) * 0.4}s`,
            }}
          />
        ))}
      </div>

      <div className="container dashboard__inner">
        <header className="dashboard__welcome">
          <p className="eyebrow dashboard__eyebrow">You're in</p>
          <h1 className="dashboard__title">
            Welcome,{" "}
            <span className="dashboard__title-name">{firstName}</span>
          </h1>
          <p className="lede dashboard__lede">
            We're building toward a unified AI partner across the verticals that matter to you.
            Here's what's coming.
          </p>
        </header>

        <ul className="dashboard__offerings">
          {OFFERINGS.map((o, i) => (
            <li
              key={o.key}
              className="dashboard__offering"
              style={{ animationDelay: `${300 + i * 120}ms` }}
            >
              <button type="button" className="offering" disabled aria-disabled="true">
                <span className="offering__icon">{o.icon}</span>
                <span className="offering__body">
                  <span className="offering__title">{o.title}</span>
                  <span className="offering__blurb">{o.blurb}</span>
                </span>
                <span className="offering__tag">Coming soon</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// Pipeline validation test — Mon May 25 19:22:28 EDT 2026
